import { useState, useCallback, useRef } from 'react';
import { RoutesApi, POIResult } from '@/shared/api/routes';
import { useAuth } from '@/modules/auth/hooks/useAuth';

export interface POISearchState {
  poiResults: POIResult[];
  isSearching: boolean;
  selectedPOI: POIResult | null;
  searchError: string | null;
}

export interface POISearchParams {
  latitude: number;
  longitude: number;
  categories: string[];
  radius: number;
  legalOnly?: boolean; // For water sources: filter by drinking_water:legal=yes
}

export interface POISearchAlongRouteParams {
  routeId: string;
  categories: string[];
  maxDistance: number;
  legalOnly?: boolean; // For water sources: filter by drinking_water:legal=yes
  tripId?: string; // Trip ID for trip-centric route lookup
  tripDay?: number; // Trip day for trip-centric route lookup
}

export function usePOISearch() {
  const { getAccessToken } = useAuth();
  const apiRef = useRef<RoutesApi | null>(null);
  const cacheRef = useRef<Map<string, POIResult[]>>(new Map());

  // Initialize API client
  if (!apiRef.current) {
    apiRef.current = new RoutesApi(getAccessToken);
  }

  const [state, setState] = useState<POISearchState>({
    poiResults: [],
    isSearching: false,
    selectedPOI: null,
    searchError: null
  });

  /**
   * Search for POIs near a specific coordinate
   */
  const searchPOI = useCallback(async (params: POISearchParams): Promise<POIResult[]> => {
    if (!apiRef.current) {
      throw new Error('API client not initialized');
    }

    setState(prev => ({ ...prev, isSearching: true, searchError: null }));

    try {
      // Create cache key (include legalOnly in cache key)
      const cacheKey = `${params.latitude}-${params.longitude}-${params.categories.join(',')}-${params.radius}-${params.legalOnly ?? false}`;
      
      // Check cache first
      if (cacheRef.current.has(cacheKey)) {
        const cachedResults = cacheRef.current.get(cacheKey)!;
        setState(prev => ({ 
          ...prev, 
          poiResults: cachedResults, 
          isSearching: false 
        }));
        return cachedResults;
      }

      const results = await apiRef.current.searchPOI(
        params.latitude,
        params.longitude,
        params.categories,
        params.radius,
        params.legalOnly
      );

      // Cache the results
      cacheRef.current.set(cacheKey, results);
      
      // Limit cache size to prevent memory issues
      if (cacheRef.current.size > 20) {
        const firstKey = cacheRef.current.keys().next().value;
        if (firstKey) {
          cacheRef.current.delete(firstKey);
        }
      }

      setState(prev => ({ 
        ...prev, 
        poiResults: results, 
        isSearching: false 
      }));

      return results;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to search POIs';
      setState(prev => ({ 
        ...prev, 
        isSearching: false, 
        searchError: errorMessage 
      }));
      throw error;
    }
  }, []);

  /**
   * Search for POIs along a route
   */
  const searchPOIAlongRoute = useCallback(async (params: POISearchAlongRouteParams): Promise<POIResult[]> => {
    if (!apiRef.current) {
      throw new Error('API client not initialized');
    }

    setState(prev => ({ ...prev, isSearching: true, searchError: null }));

    try {
      const results = await apiRef.current.searchPOIAlongRoute(
        params.routeId,
        params.categories,
        params.maxDistance,
        params.legalOnly,
        params.tripId,
        params.tripDay
      );

      setState(prev => ({ 
        ...prev, 
        poiResults: results, 
        isSearching: false 
      }));

      return results;
    } catch (error) {
      let errorMessage = 'Failed to search POIs along route';
      
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('Route not found')) {
          errorMessage = 'Route not found. Please select a valid route and try again.';
        } else if (error.message.includes('403') || error.message.includes('Access denied')) {
          errorMessage = 'You do not have permission to access this route. Please select a different route.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Authentication error. Please refresh the page and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setState(prev => ({ 
        ...prev, 
        isSearching: false, 
        searchError: errorMessage 
      }));
      throw error;
    }
  }, []);

  /**
   * Save a POI as a waypoint to a specific trip
   */
  const savePOIAsWaypoint = useCallback(async (
    poi: POIResult,
    tripId: string,
    routeId: string,
    notes?: string
  ): Promise<void> => {
    if (!apiRef.current) {
      throw new Error('API client not initialized');
    }

    const waypointData = {
      trip_id: tripId,
      route_id: routeId,
      name: poi.name,
      latitude: poi.latitude,
      longitude: poi.longitude,
      notes: notes || poi.metadata?.description || '',
      poi_type: poi.poi_type as any,
      poi_mapbox_id: poi.id,
      poi_address: poi.address,
      poi_metadata: poi.metadata,
      // Assign source and folder will be set automatically by backend
      source: 'poi_search' as const,
      // Mark as POI (non-itinerary waypoint)
      is_poi: true
    };

    await apiRef.current.createWaypoint(waypointData);
  }, []);

  /**
   * Get place name for coordinates (reverse geocoding)
   */
  const getPlaceName = useCallback(async (longitude: number, latitude: number): Promise<string> => {
    if (!apiRef.current) {
      throw new Error('API client not initialized');
    }

    try {
      const result = await apiRef.current.reverseGeocode(longitude, latitude);
      return result.name;
    } catch (error) {
      return 'Unknown location';
    }
  }, []);

  /**
   * Select a POI for preview/details
   */
  const selectPOI = useCallback((poi: POIResult | null) => {
    setState(prev => ({ ...prev, selectedPOI: poi }));
  }, []);

  /**
   * Clear search results
   */
  const clearResults = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      poiResults: [], 
      selectedPOI: null, 
      searchError: null 
    }));
  }, []);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  return {
    // State
    ...state,
    
    // Actions
    searchPOI,
    searchPOIAlongRoute,
    savePOIAsWaypoint,
    getPlaceName,
    selectPOI,
    clearResults,
    clearCache
  };
}
